/* eslint-disable react-hooks/exhaustive-deps */
import { AuthData, getAuthDocPath, MinimalOrgAuth, UserInfo } from '@cbo/shared-library';
import { Organization } from '@ncr-voyix-commerce/react-common-components';
import { useOktaAuth } from '@okta/okta-react';
import { FullStory, isInitialized } from '@fullstory/browser';
import { User as FirebaseUser, getAuth, onIdTokenChanged, signOut } from 'firebase/auth';
import { deleteToken, getMessaging, getToken, isSupported, MessagePayload, onMessage } from 'firebase/messaging';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import routes from '../constants/routes';
import { useCreateSelfEmployeeMutation } from '../labor/requests/mutations';
import { useEmployeeDetailsSelfQuery, useGetEmployeeByEmployeeIdQuery } from '../labor/requests/queries';
import { EmploymentStatus } from '../labor/types';
import useFirebaseTokenExchange from '../lib/auth';
import checkUserRoleForEmployment from '../lib/employee';
import { AuthStatus, User } from '../models/User';
import UserRole from '../models/UserRole';
import { useOrgContext, useUserProfileContextInternal } from '../org/CommonComponentWrapper';
import useDocumentSubscription from '../utils/hooks/useDocumentSubscription';
import { useFirebaseApi } from './firebaseApiContext';
import { useNotifications } from './notificationContext';
import { IApiError } from '../services/types';
import useCfcOrg from '../utils/hooks/useCfcOrg';

// Constants
const RETRY_DELAY_MS = 2000;
const MAX_RETRY_ATTEMPTS = 3;
const REFETCH_DELAY_MS = 100;
// Maximum number of times the on-load refresh will be retried after failures before
// giving up until the next identity change (logout/re-login or org switch).
const MAX_ON_LOAD_RETRIES = 3;

// Module-level set of identity keys for which the on-load refresh has already succeeded
// this page load. Using a module-level variable (rather than a ref or sessionStorage)
// ensures the guard:
//   • survives React component remounts within the same page load (e.g. Strict Mode)
//   • is reset on every browser page refresh (module re-evaluation), so permission
//     changes made in the NCRID Portal are always picked up on the next page load.
export const refreshedOnLoadKeys = new Set<string>();

export const baseUser: Readonly<User> = {
  fullyAuthenticated: 'undetermined',
  role: UserRole.None,
  oktaStatus: 'undetermined',
  firebaseStatus: 'undetermined',
  firebaseToken: null,
  oktaToken: null,
  id: null,
  profile: null,
  org: null,
  isOrgSwitching: false,
  fcmToken: null,
  bslAuth: null,
  employmentStatus: process.env.REACT_APP_USE_PREVIEW_ORG_SWITCHER === 'true' ? EmploymentStatus.ACTIVE : null,
};

export const UserContext = createContext<User>({
  ...baseUser,
});

UserContext.displayName = 'UserContext';

export const useUsers = () => useContext(UserContext);

type UserContextProviderProps = {
  children: React.ReactNode;
  vapidKey: string;
};

/**
 * Resets Firebase-related user properties to base state
 * @param user - Current user state
 * @param firebaseStatus - New Firebase auth status
 * @returns Updated user object with Firebase properties reset
 */
function unsetFirebase(user: User, firebaseStatus: AuthStatus): User {
  return {
    ...user,
    firebaseStatus,
    firebaseToken: baseUser.firebaseToken,
    id: baseUser.id,
    fullyAuthenticated: 'undetermined',
    org: baseUser.org,
    profile: baseUser.profile,
    role: baseUser.role,
  };
}

/**
 * Resets Okta-related user properties to base state
 * @param user - Current user state
 * @param oktaStatus - New Okta auth status
 * @returns Updated user object with Okta properties reset
 */
function unsetOkta(user: User, oktaStatus: AuthStatus): User {
  return {
    ...user,
    fullyAuthenticated: 'undetermined',
    oktaStatus,
  };
}

const refreshMinimum = 1000 * 60 * 5; // Refresh at least five minutes before expiration
const refreshOffset = Math.random() * 1000 * 60 * 3; // Up to three minute random offset for refreshes so that every open browser does not send a refresh request simultaneously

/**
 * Calculates when to refresh authentication token
 * @param expires - Token expiration time in milliseconds
 * @returns Duration in milliseconds until refresh should occur
 */
export function whenToRefresh(expires: number): number {
  const refreshTarget = expires - refreshMinimum - refreshOffset;
  const duration = refreshTarget - Date.now();
  return Math.max(duration, 0);
}

function isUserContextStable(user: User, organization: Organization | undefined): boolean {
  return !user.isOrgSwitching && user.bslAuth !== null && organization !== undefined;
}

function UserContextProvider({ children, vapidKey }: UserContextProviderProps) {
  const [user, setUser] = useState<User>({ ...baseUser });
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null | undefined>(undefined);
  const [firebaseTokenOrgId, setFirebaseTokenOrgId] = useState<string | null | undefined>(undefined);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [fullEmployeeRetryCount, setFullEmployeeRetryCount] = useState<number>(0);
  const location = useLocation();
  const { oktaAuth, authState: oktaAuthState } = useOktaAuth();
  const { setUserDisplayName } = useUserProfileContextInternal();
  const notifications = useNotifications();
  const { organization } = useOrgContext();
  const [currentOrg, setCurrentOrg] = useState(organization);
  const firebaseTokenExchange = useFirebaseTokenExchange();
  // Consolidated state for token exchange management
  const tokenExchangeStateRef = useRef<{
    lastExchange: { accessToken: string; orgId: string } | null;
    isExchanging: boolean;
    pendingOrgId: string | null;
  }>({ lastExchange: null, isExchanging: false, pendingOrgId: null });
  // Tracks whether the on-load refreshAuth call has already succeeded this session.
  // The ref is the primary guard; sessionStorage is a secondary guard that survives
  // component remounts (e.g. React Strict Mode double-invocation, Suspense retries)
  // within the same browser tab and auth identity.
  const hasRefreshedOnLoad = useRef(false);
  // Generation counter: incremented each time a new call starts or an old one is cancelled.
  // Each async call captures its generation at the moment it fires; the .then/.finally
  // callbacks compare against the current value and become no-ops if it has advanced,
  // preventing a superseded call from writing to shared state regardless of settle order.
  const refreshOnLoadGeneration = useRef(0);
  // Number of consecutive failures for the on-load refresh. Capped at MAX_ON_LOAD_RETRIES
  // to avoid hammering the backend when bslAuth repeatedly updates after a failure.
  const refreshOnLoadRetryCount = useRef(0);
  // Memoize the result of checkUserRoleForEmployment(user)
  const isEmployeeRole = useMemo(() => checkUserRoleForEmployment(user), [user.bslAuth, user.role]);
  const { isCfcOrg, isPosDataLoading } = useCfcOrg(user.fullyAuthenticated === 'authenticated');

  const {
    data: selfEmployeeData,
    status: selfEmployeeStatus,
    error: selfEmployeeError,
    refetch: selfEmployeeRefetch,
  } = useEmployeeDetailsSelfQuery(
    user.fullyAuthenticated === 'authenticated' &&
      !isCfcOrg &&
      !isPosDataLoading &&
      isEmployeeRole &&
      isUserContextStable(user, organization)
  );

  useEffect(() => {
    // refetch self employee data when isEmployeeRole changes to true
    // Only refetch if we have a stable organization context and are not in the middle of org switching
    // Skip refetch for CFC orgs as they cannot have employee records
    if (isEmployeeRole && organization && isUserContextStable(user, organization) && user.firebaseToken && !isCfcOrg) {
      // Add a small delay to ensure Firebase token exchange is complete
      const timer = setTimeout(() => {
        selfEmployeeRefetch();
      }, REFETCH_DELAY_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [organization?.id, user.isOrgSwitching, user.bslAuth, user.firebaseToken, isEmployeeRole, isCfcOrg]);

  // Only enable full employee query after selfEmployee query is complete and employeeId is stable
  const shouldFetchFullEmployee =
    selfEmployeeStatus === 'success' &&
    !!selfEmployeeData?.employeeId &&
    selfEmployeeData.employeeId.trim() !== '' &&
    !selfEmployeeError &&
    user.fullyAuthenticated === 'authenticated' &&
    isEmployeeRole &&
    isUserContextStable(user, organization);

  const {
    data: fullEmployeeData,
    isLoading: isEmployeeLoading,
    error: fullEmployeeError,
    refetch: refetchFullEmployee,
  } = useGetEmployeeByEmployeeIdQuery(selfEmployeeData?.employeeId || '', shouldFetchFullEmployee, null, false);

  // Effect to handle 404 errors on full employee data and retry
  useEffect(() => {
    if (fullEmployeeError && shouldFetchFullEmployee && fullEmployeeRetryCount < MAX_RETRY_ATTEMPTS) {
      const err = fullEmployeeError as IApiError;
      const is404Error = err.details?.statusCode === 404 || Number(err.status) === 404;

      if (is404Error) {
        // Retry after a delay
        const retryTimer = setTimeout(async () => {
          setFullEmployeeRetryCount((prev) => prev + 1);
          await selfEmployeeRefetch();
          await refetchFullEmployee();
        }, RETRY_DELAY_MS);

        return () => clearTimeout(retryTimer);
      }
    }
    return undefined;
  }, [
    fullEmployeeError,
    shouldFetchFullEmployee,
    refetchFullEmployee,
    selfEmployeeData?.employeeId,
    fullEmployeeRetryCount,
    user.bslAuth,
    organization?.id,
    user.org?.bslId,
  ]);

  // Reset retry count when employee ID changes or when data is successfully fetched
  useEffect(() => {
    if (fullEmployeeData || selfEmployeeData?.employeeId) {
      setFullEmployeeRetryCount(0);
    }
  }, [fullEmployeeData, selfEmployeeData?.employeeId]);

  const createSelfEmployeeInfo = useCreateSelfEmployeeMutation();
  const { refreshAuth } = useFirebaseApi();
  const authDocPath = useMemo(() => {
    if (!firebaseUser?.uid || !user.org?.bslId || (!organization && !currentOrg)) {
      return undefined;
    }
    // Ensure we use the correct organization during switching
    const targetOrgId = user.isOrgSwitching ? organization?.id : currentOrg?.id ?? organization?.id;
    return getAuthDocPath(firebaseUser.uid, targetOrgId ?? '');
  }, [firebaseUser?.uid, user.org?.bslId, user.firebaseToken, organization, currentOrg, user.isOrgSwitching]);

  const authDocSnapshot = useDocumentSubscription<AuthData>(authDocPath);
  // Identity key used with refreshedOnLoadKeys to deduplicate the on-load refresh across
  // React remounts (e.g. Strict Mode). Derived from uid + orgId, both available synchronously.
  const onLoadRefreshKey = useMemo(
    () => (firebaseUser?.uid && organization?.id ? `${firebaseUser.uid}:${organization.id}` : null),
    [firebaseUser?.uid, organization?.id]
  );

  /**
   * Determines the overall authentication status based on Firebase, Okta, and BSL auth states
   * @param firebaseStatus - Firebase authentication status
   * @param oktaStatus - Okta authentication status
   * @param bslAuth - BSL authentication data
   * @returns Overall authentication status
   */
  const isUserFullyAuthenticated = (
    firebaseStatus: AuthStatus,
    oktaStatus: AuthStatus,
    bslAuth: AuthData | null
  ): AuthStatus => {
    if (oktaStatus === 'undetermined') {
      return 'undetermined';
    }

    if (oktaStatus === 'unauthenticated') {
      return 'unauthenticated';
    }

    if (firebaseStatus === 'undetermined' || firebaseStatus === 'unauthenticated' || bslAuth === null) {
      return 'undetermined';
    }

    if (oktaStatus === 'authenticated' && firebaseStatus === 'authenticated' && bslAuth) {
      return 'authenticated';
    }

    throw new Error('Error determining authentication');
  };

  // Listen for Auth Document
  useEffect(() => {
    if (organization && user.isOrgSwitching) {
      setCurrentOrg(organization);
    }
    const bslAuth = authDocSnapshot?.data() ?? null;
    setUser((currentUser) => ({ ...currentUser, bslAuth }));
  }, [authDocSnapshot]);

  useEffect(() => {
    const oktaJwt = user.oktaToken;
    if (user.bslAuth == null || refreshAuth == null || oktaJwt == null) {
      return () => undefined;
    }
    const refresh = whenToRefresh(user.bslAuth.expires.toMillis());
    const future = setTimeout(() => refreshAuth({ oktaJwt }), refresh);
    return () => clearTimeout(future);
  }, [user.bslAuth, refreshAuth, user.oktaToken]);

  // Tracks the previous onLoadRefreshKey so the identity-reset effect can remove the
  // correct key from refreshedOnLoadKeys when the identity changes (not on first assignment).
  const previousOnLoadRefreshKeyRef = useRef<string | null>(null);

  // Reset the on-load refresh guards whenever the auth identity changes (logout/login or org
  // switch) so that a new session always triggers a fresh permission refresh.
  useEffect(() => {
    const previousKey = previousOnLoadRefreshKeyRef.current;
    previousOnLoadRefreshKeyRef.current = onLoadRefreshKey;
    // Only remove the module-set entry when transitioning away from a key — not on first
    // assignment (null → key), which would clear the entry the prior mount just wrote.
    if (previousKey && previousKey !== onLoadRefreshKey) {
      refreshedOnLoadKeys.delete(previousKey);
    }
    hasRefreshedOnLoad.current = false;
    refreshOnLoadRetryCount.current = 0;
  }, [onLoadRefreshKey]);

  // On every page load, call refreshAuth once as soon as bslAuth is available so that any
  // permission changes made in the NCRID Portal (e.g. removing App Admin) are reflected
  // immediately without requiring the user to log out and back in.
  useEffect(() => {
    // Wait until onLoadRefreshKey is non-null (firebaseUser.uid + organization.id available)
    // so the sessionStorage guard can be both read and written with a stable key.
    if (!onLoadRefreshKey) {
      return () => undefined;
    }
    const oktaJwt = user.oktaToken;
    // loose equality to catch both null and undefined cases, since user state initializes these as null but they may be undefined briefly during loading
    const missingDependencies = user.bslAuth == null || refreshAuth == null || oktaJwt == null;
    // Check module-level set: if this identity already refreshed in a prior mount of this
    // component (e.g. React Strict Mode), skip without making a network call.
    if (onLoadRefreshKey && refreshedOnLoadKeys.has(onLoadRefreshKey)) {
      hasRefreshedOnLoad.current = true;
    }
    if (hasRefreshedOnLoad.current || missingDependencies) {
      return () => undefined;
    }
    // Skip when the token is already near/past expiry — the expiry-based effect will
    // call refreshAuth immediately and serves as the on-load refresh for this session.
    if (user.bslAuth && whenToRefresh(user.bslAuth.expires.toMillis()) === 0) {
      hasRefreshedOnLoad.current = true;
      return () => undefined;
    }
    // Stop retrying after MAX_ON_LOAD_RETRIES consecutive failures to avoid hammering
    // the backend. The guard resets on the next identity change (logout/re-login/org switch).
    if (refreshOnLoadRetryCount.current >= MAX_ON_LOAD_RETRIES) {
      return () => undefined;
    }
    refreshOnLoadGeneration.current += 1;
    const generation = refreshOnLoadGeneration.current;
    refreshAuth({ oktaJwt })
      .then(() => {
        if (refreshOnLoadGeneration.current === generation) {
          hasRefreshedOnLoad.current = true;
          if (onLoadRefreshKey) refreshedOnLoadKeys.add(onLoadRefreshKey);
        }
      })
      .catch((error) => {
        if (refreshOnLoadGeneration.current === generation) {
          refreshOnLoadRetryCount.current += 1;
          // eslint-disable-next-line no-console
          console.warn(
            `Failed to refresh auth on page load (attempt ${refreshOnLoadRetryCount.current} of ${MAX_ON_LOAD_RETRIES}). Permissions may be stale until the next identity change (logout, re-login, or org switch).`,
            error
          );
        }
      });
    return () => {
      // Advancing the generation invalidates this call's .then callback, ensuring a
      // superseded call cannot mark the refresh as done or interfere with the next attempt.
      refreshOnLoadGeneration.current += 1;
    };
  }, [user.bslAuth, user.oktaToken, refreshAuth, onLoadRefreshKey]);

  // Setup listener to Firebase Auth State
  // Firebase SDK automatically persists auth in browser storage and restores it on page reload
  // This listener fires immediately on mount if a valid session exists, bypassing token re-exchange
  useEffect(() => onIdTokenChanged(getAuth(), setFirebaseUser), []);

  // Update User when firebase user is updated
  useEffect(() => {
    switch (firebaseUser) {
      case undefined: // user login state is undetermined
        setFirebaseTokenOrgId(undefined);
        setUser((currentUser) => unsetFirebase(currentUser, 'undetermined'));
        break;
      case null: // user has logged out
        setFirebaseTokenOrgId(null);
        setUser((currentUser) => unsetFirebase(currentUser, 'unauthenticated'));
        break;
      default:
        firebaseUser.getIdTokenResult().then((idToken) => {
          const newOrgId = (idToken.claims.org as MinimalOrgAuth)?.bslId ?? null;

          setFirebaseTokenOrgId(newOrgId);

          // Seed org information from persisted Firebase session on page reload
          // so org-switch detection works even when no token exchange has run yet.
          if (!tokenExchangeStateRef.current.lastExchange && newOrgId) {
            tokenExchangeStateRef.current.lastExchange = { accessToken: '', orgId: newOrgId };
          }

          // Check if this token update completes an org switch
          if (tokenExchangeStateRef.current.pendingOrgId && newOrgId === tokenExchangeStateRef.current.pendingOrgId) {
            tokenExchangeStateRef.current.pendingOrgId = null;
          }

          setUser((currentUser) => ({
            ...currentUser,
            firebaseStatus: 'authenticated',
            firebaseToken: idToken.token,
            id: (idToken.claims.user_id as string) ?? '',
            org: (idToken.claims.org as MinimalOrgAuth) ?? null,
            profile: (idToken.claims.user as UserInfo) ?? null,
            role: UserRole.None,
          }));
        });
        break;
    }
  }, [firebaseUser]);

  useEffect(() => {
    setUserDisplayName(user.profile?.name ?? '');

    if (isInitialized() && user.profile?.name) {
      FullStory('setProperties', {
        type: 'user',
        properties: {
          displayName: user.profile?.name,
          email: user.profile?.email,
        },
      });
    }
  }, [user.profile]);

  // Update User when Okta user is updated
  useEffect(() => {
    if (!oktaAuthState || location.pathname === routes.LOGIN) {
      setUser((currentUser) => unsetOkta(currentUser, 'undetermined'));
    } else if (oktaAuthState.isAuthenticated === false) {
      setUser((currentUser) => unsetOkta(currentUser, 'unauthenticated'));
    } else {
      setUser((currentUser) => ({
        ...currentUser,
        oktaStatus: 'authenticated',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaAuth, oktaAuthState, user.oktaStatus, location.pathname]);

  useEffect(() => {
    setUser((currentUser) => ({
      ...currentUser,
      oktaToken: oktaAuthState?.accessToken?.accessToken ?? null,
    }));
  }, [oktaAuthState?.accessToken?.accessToken]);

  // This effect makes sure that we are logged into the firebase account
  // that matches the okta account email.
  useEffect(() => {
    async function getFCMToken() {
      if (!(await isSupported())) {
        // eslint-disable-next-line no-console
        console.warn('Messaging is not supported in your browser');
        return;
      }
      const messaging = getMessaging();
      getToken(messaging, {
        vapidKey,
      })
        .then((token) => {
          if (token) {
            setFcmToken(token);
            onMessage(messaging, (payload: MessagePayload) => {
              notifications.setNewNotification(payload);
            });
          }
        })
        .catch((e) => {
          // the user didn't allow access to notifications
        });
    }

    // Wait for Firebase SDK to resolve initial auth state before attempting token exchange
    if (firebaseUser === undefined) {
      return;
    }

    if (user.firebaseStatus === 'authenticated' && user.oktaStatus === 'unauthenticated') {
      // If we have a firebase email that does not match an okta email
      // then we need to log out of firebase
      signOut(getAuth()).then(() => {
        deleteToken(getMessaging());
        setFcmToken(null);
        tokenExchangeStateRef.current = { lastExchange: null, isExchanging: false, pendingOrgId: null };
      });
    } else if (user.oktaStatus === 'authenticated' && oktaAuthState?.accessToken?.accessToken && organization) {
      // Token Exchange Logic
      // This only runs when Firebase SDK auth token is invalid or missing:
      // - FIRST LOGIN: Firebase has no persisted session, exchange required
      // - SUBSEQUENT RELOADS: Firebase SDK restores session automatically, no exchange needed
      // - ORG SWITCH: User changes organization, re-exchange with new org context needed
      // - FIREBASE AUTH INVALID: Firebase SDK will handle automatic token refresh via onIdTokenChanged
      //
      // Note: We do NOT exchange on Okta token refresh - Firebase manages its own token lifecycle
      const { accessToken } = oktaAuthState.accessToken;
      const orgId = organization.id;

      // Detect if we're switching organizations by comparing selected org with current Firebase token org.
      // `lastExchange` is used as a short-term guard to prevent duplicate exchanges before token claims update.
      const tokenOrgMismatch =
        firebaseUser !== null && firebaseTokenOrgId !== undefined && firebaseTokenOrgId !== orgId;
      const alreadyExchangedForOrg = tokenExchangeStateRef.current.lastExchange?.orgId === orgId;
      const isOrgChange = tokenOrgMismatch && !alreadyExchangedForOrg;

      // Only exchange token when Firebase SDK auth is actually invalid:
      // 1. Firebase is not authenticated AND no valid Firebase user exists in SDK
      //    (user.firebaseStatus !== 'authenticated' && !firebaseUser)
      //    On page reload, firebaseUser is set immediately by Firebase SDK from browser storage,
      //    even if user.firebaseStatus is still 'undetermined', so we check firebaseUser to avoid
      //    unnecessary token exchanges on reload
      // 2. Organization has changed (requires new claims with different org context)
      // 3. Not currently in the middle of an exchange
      const shouldExchangeToken =
        !tokenExchangeStateRef.current.isExchanging &&
        ((user.firebaseStatus !== 'authenticated' && firebaseUser === null) || isOrgChange);

      if (shouldExchangeToken) {
        tokenExchangeStateRef.current.isExchanging = true;
        tokenExchangeStateRef.current.pendingOrgId = orgId;
        // Use the organization from useOrgContext directly as the preferred org
        firebaseTokenExchange(accessToken, organization)
          .then(() => {
            tokenExchangeStateRef.current.lastExchange = { accessToken, orgId };
            tokenExchangeStateRef.current.isExchanging = false;
            // Only get FCM token on initial login, not on org switch
            if (!isOrgChange) {
              // Defer FCM token request to avoid blocking token exchange completion
              Promise.resolve().then(() => getFCMToken());
            }
            // Note: pendingOrgId will be cleared when firebaseUser updates
          })
          .catch((error) => {
            tokenExchangeStateRef.current.isExchanging = false;
            tokenExchangeStateRef.current.pendingOrgId = null;
          });
      }
    }
  }, [
    user.firebaseStatus,
    user.oktaStatus,
    oktaAuthState?.accessToken?.accessToken,
    organization,
    firebaseUser,
    firebaseTokenOrgId,
    vapidKey,
    firebaseTokenExchange,
  ]);

  useEffect(() => {
    setUser((oldUser) => ({
      ...oldUser,
      fcmToken,
    }));
  }, [fcmToken]);

  useEffect(() => {
    const authenticatedState: AuthStatus = isUserFullyAuthenticated(user.firebaseStatus, user.oktaStatus, user.bslAuth);
    setUser((currentUser) => ({
      ...currentUser,
      fullyAuthenticated: authenticatedState,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.firebaseStatus, user.oktaStatus, user.bslAuth]);

  // Memoize bslOrganization to prevent unnecessary re-renders
  const bslOrganization = useMemo(
    (): MinimalOrgAuth => ({
      bslLongName: organization?.organizationName ?? '',
      bslId: organization?.id ?? '',
      displayName: organization?.displayName ?? '',
    }),
    [organization?.organizationName, organization?.id, organization?.displayName]
  );

  useEffect(() => {
    const isOrganizationSwitching: boolean =
      (!!organization && !!currentOrg && organization.id !== currentOrg.id) ||
      tokenExchangeStateRef.current.pendingOrgId !== null;
    setUser((currentUser) => ({
      ...currentUser,
      isOrgSwitching: isOrganizationSwitching,
      org: bslOrganization,
    }));
  }, [organization, currentOrg, bslOrganization, user.firebaseToken]);

  // This effect handles fetching and creating initial information for an employee user
  useEffect(() => {
    // Check that the user is authenticated and has one of the roles that requires fetching information
    // Also ensure we're not in the middle of an organization switch and have stable auth context
    if (
      user.fullyAuthenticated === 'authenticated' &&
      checkUserRoleForEmployment(user) &&
      selfEmployeeStatus !== 'loading' &&
      isUserContextStable(user, organization)
    ) {
      const employeeDoesNotExist = selfEmployeeError && (selfEmployeeError as IApiError).details.statusCode === 404;
      // If the employee info already exists, update id for user context
      if (!selfEmployeeError && selfEmployeeData) {
        setUser((currentUser) => ({
          ...currentUser,
          id: selfEmployeeData.employeeId,
        }));
      }
      // If the employee info does not exist, create it and update id for user context
      if (employeeDoesNotExist && !isCfcOrg && user.profile) {
        createSelfEmployeeInfo.mutate(
          {
            ncrUserId: user.profile.userId,
            lastName: user.profile.lastName,
            firstName: user.profile.firstName,
            emailAddress: user.profile.email,
          },
          {
            onSuccess: (data) =>
              data?.employeeId && setUser((currentUser) => ({ ...currentUser, id: data.employeeId })),
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selfEmployeeStatus,
    selfEmployeeError,
    selfEmployeeData,
    user.fullyAuthenticated,
    user.bslAuth?.personas,
    user.isOrgSwitching,
    organization,
    isCfcOrg,
  ]);

  // Effect to handle employment status updates
  useEffect(() => {
    if (user.fullyAuthenticated !== 'authenticated') return;

    const requiresEmployment = checkUserRoleForEmployment(user);

    const shouldDefaultActive = !requiresEmployment || (requiresEmployment && isCfcOrg);

    if (shouldDefaultActive) {
      if (user.employmentStatus !== EmploymentStatus.ACTIVE) {
        setUser((currentUser) => ({ ...currentUser, employmentStatus: EmploymentStatus.ACTIVE }));
      }
      return;
    }

    // For normal orgs with an employee role, use the fetched employment status
    if (!isEmployeeLoading && fullEmployeeData && user.employmentStatus !== fullEmployeeData.employmentStatus) {
      setUser((currentUser) => ({ ...currentUser, employmentStatus: fullEmployeeData.employmentStatus }));
    }
  }, [fullEmployeeData, isEmployeeLoading, user.fullyAuthenticated, user.bslAuth?.roles, isCfcOrg]);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

export default UserContextProvider;
